import { Cable, Layers, AlertTriangle, Shield, Zap, RefreshCw, ArrowUpRight } from "lucide-react";
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const GOLD = "#F5A623";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Service = {
  number: string;
  title: string;
  desc: string;
  bullets: string[];
  icon: typeof Cable;
  span: "wide" | "normal";
  accent?: boolean;
};
const SERVICES: Service[] = [{
  number: "01",
  title: "Snapped Cable Replacement",
  desc: "Both cables replaced in pairs — never just one. Heavy-duty 7×19 aircraft-grade galvanised steel cable, rated for residential and commercial doors.",
  bullets: ["Pair replacement (left + right)", "Galvanised aircraft-grade cable", "Tension balanced both sides"],
  icon: Cable,
  span: "wide",
  accent: true
}, {
  number: "02",
  title: "Frayed Cable Service",
  desc: "Replace before it snaps. Catch it early.",
  bullets: ["Pre-failure inspection", "Rust treatment", "Same-day swap"],
  icon: AlertTriangle,
  span: "normal"
}, {
  number: "03",
  title: "Drum & Cable Re-spool",
  desc: "Cable off the drum — usually a spring or drum issue.",
  bullets: ["Drum inspection", "Correct cable wrap", "Spring tension check"],
  icon: RefreshCw,
  span: "normal"
}, {
  number: "04",
  title: "Cable + Spring Combo",
  desc: "When a cable snaps, the spring is usually next. We replace both together so you don't pay two callouts and your door is properly balanced from day one.",
  bullets: ["Bundled discount pricing", "Single callout convenience", "Full door re-balance"],
  icon: Layers,
  span: "wide"
}, {
  number: "05",
  title: "Safety Cable Install",
  desc: "Extension-spring doors need safety cables — legally required.",
  bullets: ["Compliant install", "Prevents spring whip", "Insurance peace of mind"],
  icon: Shield,
  span: "normal"
}, {
  number: "06",
  title: "Heavy-Duty Commercial",
  desc: "Warehouse, strata, industrial doors. Heavier cable spec.",
  bullets: ["8mm+ commercial cable", "Strata-approved invoicing", "After-hours scheduling"],
  icon: Zap,
  span: "normal"
}];
export default function CableServices() {
  return <section style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      {/* Atmospheric gradient */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      background: `radial-gradient(circle at 20% 0%, rgba(245,166,35,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(245,166,35,0.04) 0%, transparent 50%)`,
      pointerEvents: "none"
    }} />

      {/* Grain overlay */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      opacity: 0.03,
      mixBlendMode: "overlay",
      pointerEvents: "none",
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1280,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 72,
        alignItems: "end"
      }} className="cable-services-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                02 / Cable services
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              Every cable problem,{" "}
              <span style={{
              color: GOLD,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                solved on the first visit.
              </span>
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: "rgba(255,255,255,0.7)",
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 480,
          justifySelf: "end"
        }}>
            We stock aircraft-grade cables for every residential and commercial
            door in the van — most jobs done same-day, no return trip.
          </p>
        </div>

        {/* === BENTO GRID === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: 20
      }} className="cable-services-grid">
          {SERVICES.map((s, i) => <ServiceCard key={i} service={s} />)}
        </div>

        {/* === BOTTOM CTA STRIP === */}
        <div style={{
        marginTop: 56,
        padding: "32px 40px",
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 4,
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div style={{
          fontFamily: FONT_HEAD,
          fontSize: 18,
          fontWeight: 500,
          color: "rgba(255,255,255,0.9)",
          lineHeight: 1.4
        }}>
            Not sure if it's the cable?{" "}
            <span style={{
            color: GOLD,
            fontWeight: 700
          }}>
              Send us a 10-second video
            </span>{" "}
            and we'll diagnose it free.
          </div>

          <a href="https://wa.me/61485000933?text=Hi%20Gold%20Garage%20Door%2C%20here%27s%20a%20video%20of%20my%20garage%20door%20cable." target="_blank" rel="noopener noreferrer" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "14px 22px",
          background: GOLD,
          color: NAVY,
          fontFamily: FONT_HEAD,
          fontSize: 14,
          fontWeight: 700,
          borderRadius: 8,
          textDecoration: "none",
          whiteSpace: "nowrap",
          transition: "transform 0.2s, box-shadow 0.2s",
          boxShadow: "0 6px 20px rgba(245,166,35,0.3)"
        }} onMouseEnter={e => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.45)";
        }} onMouseLeave={e => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.3)";
        }}>
            Send video via WhatsApp
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 1024px) {
          .cable-services-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .cable-services-grid > * {
            grid-column: span 1 !important;
          }
        }
        @media (max-width: 700px) {
          .cable-services-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .cable-services-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>;
}

// === Bento Card ===
function ServiceCard({
  service
}: {
  service: Service;
}) {
  const Icon = service.icon;
  const colSpan = service.span === "wide" ? 4 : 2;
  return <div className="cable-service-card" style={{
    gridColumn: `span ${colSpan}`,
    position: "relative",
    padding: "36px 32px",
    background: service.accent ? `linear-gradient(135deg, ${NAVY_DEEP} 0%, ${NAVY} 100%)` : "rgba(255,255,255,0.025)",
    border: service.accent ? `1px solid ${GOLD}` : "1px solid rgba(255,255,255,0.08)",
    borderRadius: 4,
    cursor: "default",
    transition: "transform 0.3s, border-color 0.3s, background 0.3s",
    overflow: "hidden",
    minHeight: 240
  }} onMouseEnter={e => {
    const el = e.currentTarget as HTMLDivElement;
    el.style.transform = "translateY(-4px)";
    if (!service.accent) {
      el.style.borderColor = "rgba(245,166,35,0.4)";
      el.style.background = "rgba(255,255,255,0.045)";
    }
  }} onMouseLeave={e => {
    const el = e.currentTarget as HTMLDivElement;
    el.style.transform = "translateY(0)";
    if (!service.accent) {
      el.style.borderColor = "rgba(255,255,255,0.08)";
      el.style.background = "rgba(255,255,255,0.025)";
    }
  }}>
      {/* Top row */}
      <div style={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: 28
    }}>
        <span style={{
        fontFamily: FONT_HEAD,
        fontSize: 13,
        fontWeight: 800,
        color: service.accent ? GOLD : "rgba(255,255,255,0.4)",
        letterSpacing: "0.1em"
      }}>
          {service.number}
        </span>

        <div style={{
        width: 44,
        height: 44,
        borderRadius: 8,
        background: service.accent ? GOLD : "rgba(245,166,35,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
          <Icon size={22} color={service.accent ? NAVY : GOLD} strokeWidth={2} />
        </div>
      </div>

      {/* Title */}
      <h3 style={{
      fontFamily: FONT_HEAD,
      fontSize: service.span === "wide" ? 24 : 20,
      fontWeight: 700,
      color: "#ffffff",
      lineHeight: 1.2,
      letterSpacing: "-0.015em",
      margin: "0 0 12px"
    }}>
        {service.title}
      </h3>

      {/* Description */}
      <p style={{
      fontFamily: FONT_BODY,
      fontSize: 14,
      color: "rgba(255,255,255,0.65)",
      lineHeight: 1.55,
      margin: "0 0 20px"
    }}>
        {service.desc}
      </p>

      {/* Bullets */}
      <ul style={{
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8
    }}>
        {service.bullets.map((b, i) => <li key={i} style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: FONT_BODY,
        fontSize: 13,
        color: "rgba(255,255,255,0.75)"
      }}>
            <span style={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          background: GOLD,
          flexShrink: 0
        }} />
            {b}
          </li>)}
      </ul>

      {/* Accent glow */}
      {service.accent && <div aria-hidden style={{
      position: "absolute",
      bottom: -40,
      right: -40,
      width: 160,
      height: 160,
      borderRadius: "50%",
      background: `radial-gradient(circle, rgba(245,166,35,0.12) 0%, transparent 70%)`,
      pointerEvents: "none"
    }} />}
    </div>;
}